import {PageHeader} from '../ui/PageHeader';
import {ResponsiveTable} from '../ui/ResponsiveTable';
import './alpha-finance-tables.css';
import { useEffect, useState, useRef, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { api, authenticatedFetch } from '../api';
import { Empty, ErrorState } from '../ui/State';
import { alphaSave } from '../ui/alpha-save';
export function YearEndReviewAlpha({ staff = false }: { staff?: boolean }) {
  const [q] = useSearchParams(),
    building = q.get('building') ?? '',
    root = staff ? '/api/alpha/year-end/' + building : '/api/portal/accountant-reviews';
  const [data, setData] = useState<any>(),
    [selected, setSelected] = useState(''),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<object | null>(null);
  const load = () => api(root).then(setData);
  useEffect(() => {
    void load().catch(setError);
  }, [root]);
  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    pending.current ??= {
      title: f.get('title'),
      periodId: f.get('period'),
      accountantId: f.get('accountant'),
      evidenceVersionIds: f.getAll('evidence'),
      idempotencyKey: crypto.randomUUID(),
    };
    setBusy(true);
    try {
      const p = await alphaSave<{ id: string }>(pending, root, {
        method: 'POST',
        body: JSON.stringify(pending.current),
      });
      pending.current = null;
      await load();
      setSelected(p.id);
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader title="Year-end accountant review"/>
      <p>
        Review the frozen figures and supporting evidence. Proposed adjustments use the existing
        accounting journal approvals before a fresh pack is prepared.
      </p>
      {Boolean(error) && <ErrorState error={error} />}
      {data?.packs?.length===0&&<Empty>No review packs are available in your permitted records.</Empty>}
      <nav aria-label="Review packs">
        {data?.packs.map((p: any) => (
          <p key={p.id}>
            <button onClick={() => setSelected(p.id)}>
              {p.title} · Version {p.version} · {p.status}
            </button>
          </p>
        ))}
      </nav>
      {staff && data && (
        <details>
          <summary>Prepare a new review pack</summary>
          <form className="form" onSubmit={create}>
            <fieldset disabled={busy || !!pending.current}>
              <label>
                Title
                <input name="title" required minLength={3} maxLength={200} />
              </label>
              <label>
                Accounting period
                <select name="period" required>
                  <option value="">Choose period</option>
                  {data.periods.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.label} · {p.status}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Appointed accountant
                <select name="accountant" required>
                  <option value="">Choose a user with building access</option>
                  {data.accountants.map((p: any) => (
                    <option key={p.id} value={p.id}>
                      {p.display_name}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset>
                <legend>Evidence to share</legend>
                {data.documents.map((d: any) => (
                  <label key={d.id}>
                    <input type="checkbox" name="evidence" value={d.id} />
                    {d.title}
                  </label>
                ))}
              </fieldset>
            </fieldset>
            <button disabled={busy}>
              {pending.current ? 'Retry pack preparation' : 'Prepare frozen pack'}
            </button>
          </form>
        </details>
      )}
      {selected && <Review key={selected} id={selected} root={root} staff={staff} reload={load} />}
    </>
  );
}
function Review({
  id,
  root,
  staff,
  reload,
}: {
  id: string;
  root: string;
  staff: boolean;
  reload: () => Promise<unknown>;
}) {
  const [model, setModel] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    pending = useRef<{ action: string; body: object } | null>(null);
  const load = () => api(root + '/' + id).then(setModel);
  useEffect(() => {
    void load().catch(setError);
  }, [id]);
  async function save(action: string, body: object) {
    pending.current ??= { action, body: { ...body, idempotencyKey: crypto.randomUUID() } };
    setBusy(true);
    try {
      await api(root + '/' + id + '/' + pending.current.action, {
        method: 'POST',
        body: JSON.stringify(pending.current.body),
      });
      pending.current = null;
      await load();
      await reload();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  async function download(version: string) {
    try {
      const r = await authenticatedFetch(root + '/' + id + '/evidence/' + version);
      if (!r.ok) throw Error('Evidence unavailable');
      const url = URL.createObjectURL(await r.blob()),
        a = document.createElement('a');
      a.href = url;
      a.download = 'year-end-evidence';
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setError(e);
    }
  }
  if (!model) return <p>Loading review…</p>;
  const p = model.pack,
    s = p.snapshot;
  return (
    <section className="panel">
      <h2>
        {p.title} · Version {p.version}
      </h2>
      {Boolean(error) && <ErrorState error={error} />}
      <p>
        {p.status} · {s.period.label}
      </p>
      {model.stale && (
        <p role="alert">The accounting figures changed. Prepare a new pack before approval.</p>
      )}
      {s.incomplete && (
        <p role="alert">
          This pack has unresolved accounting checks and cannot receive final approval.
        </p>
      )}
      <p>{s.basis}</p>
      <dl>
        <dt>Income</dt>
        <dd>£{s.summary.income}</dd>
        <dt>Expenditure</dt>
        <dd>£{s.summary.expenditure}</dd>
        <dt>Surplus / deficit</dt>
        <dd>£{s.summary.surplus}</dd>
      </dl>

<ResponsiveTable className="alpha-finance-table">
        <caption>Frozen trial balance</caption>
        <thead>
          <tr>
            <th>Account</th>
            <th>Debit</th>
            <th>Credit</th>
          </tr>
        </thead>
        <tbody>
          {s.trial.map((r: any, i: number) => (
            <tr key={i}>
              <td>{r.name}</td>
              <td>£{r.closing_debit}</td>
              <td>£{r.closing_credit}</td>
            </tr>
          ))}
        </tbody>
      </ResponsiveTable>

      <h3>Review checks</h3>
      <ul>
        {s.checks.map((c: any) => (
          <li key={c.key}>
            {c.label}: {c.status} — {c.detail}
          </li>
        ))}
      </ul>
      <h3>Supporting evidence</h3>
      {model.evidence.map((e: any) => (
        <p key={e.id}>
          {staff ? e.title : <button onClick={() => void download(e.id)}>{e.title}</button>}
        </p>
      ))}
      <h3>Queries and responses</h3>
      {model.events.map((e: any) => (
        <article key={e.id}>
          <strong>{e.kind}</strong>
          <p>
            {e.detail}
            {e.proposed_amount && ' · £' + e.proposed_amount}
          </p>
        </article>
      ))}
      {!p.revoked_at && ['Review', 'Submitted'].includes(p.status) && (
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            void save(String(f.get('action')), {
              detail: f.get('detail'),
              ...(f.get('amount') ? { amount: f.get('amount') } : {}),
              ...(f.get('parent') ? { parentId: f.get('parent') } : {}),
              ...(f.get('disposition') ? { disposition: f.get('disposition') } : {}),
              ...(f.get('journal') ? { journalId: f.get('journal') } : {}),
            });
          }}
        >
          <fieldset disabled={busy || !!pending.current}>
            <label>
              Action
              <select name="action">
                {(staff
                  ? ['reply', 'resolve', 'approve', 'revoke']
                  : ['query', 'adjustment', 'submit']
                ).map((a) => (
                  <option key={a} value={a}>
                    {
                      {
                        reply: 'Reply to query',
                        resolve: 'Resolve query',
                        approve: 'Approve final pack',
                        revoke: 'Revoke accountant access',
                        query: 'Ask a question',
                        adjustment: 'Propose adjustment',
                        submit: 'Submit review',
                      }[a]
                    }
                  </option>
                ))}
              </select>
            </label>
            {staff ? (
              <>
                <label>
                  Query or adjustment
                  <select name="parent">
                    <option value="">Not applicable</option>
                    {model.events
                      .filter((e: any) => ['Query', 'Adjustment proposal'].includes(e.kind))
                      .map((e: any) => (
                        <option key={e.id} value={e.id}>
                          {e.detail}
                        </option>
                      ))}
                  </select>
                </label>
                <label>
                  Adjustment outcome
                  <select name="disposition">
                    <option value="">Not applicable</option>
                    <option>Not applied</option>
                    <option>Applied</option>
                  </select>
                </label>
                <label>
                  Posted journal
                  <select name="journal">
                    <option value="">Not applicable</option>
                    {model.postedJournals?.map((j: any) => (
                      <option key={j.id} value={j.id}>
                        {j.effective_date} · {j.reference}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <label>
                Proposed amount (adjustments only)
                <input name="amount" inputMode="decimal" />
              </label>
            )}
            <label>
              Review note
              <textarea name="detail" required minLength={5} maxLength={5000} />
            </label>
          </fieldset>
          <button disabled={busy}>Save review action</button>
        </form>
      )}
      {pending.current && (
        <button
          disabled={busy}
          onClick={() => void save(pending.current!.action, pending.current!.body)}
        >
          Retry the same review action
        </button>
      )}
    </section>
  );
}
